import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { cancelLeaveRequest, getMyLeaves, requestLeave } from '../lib/api';
import type { LeaveRequest, LeaveStatus, PayslipEmployeeType } from '../lib/api';

// Apply-for-leave form + the person's own leave history. Used by mentors and
// coordinators (My Leave page) and salespeople (Leave tab on their dashboard).
// Leave only counts once an admin approves it (Admin → Leave Requests), and
// approved days are filled in automatically on that month's payslip.

export const LEAVE_STATUS_STYLE: Record<LeaveStatus, { bg: string; color: string }> = {
  Pending:  { bg: '#fef3c7', color: '#92400e' },
  Approved: { bg: '#dcfce7', color: '#15803d' },
  Rejected: { bg: '#fee2e2', color: '#991b1b' },
};

const fmtDate = (d: string) =>
  new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

export const fmtLeaveDays = (n: number) => `${n} day${n === 1 ? '' : 's'}`;

// '2:00 PM' from '14:00'
export const fmtHm = (hm: string) => {
  const [h, m] = hm.split(':').map(Number);
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};

export const fmtLeaveDates = (r: Pick<LeaveRequest, 'fromDate' | 'toDate' | 'halfDay' | 'fromTime' | 'toTime'>) => {
  const half = r.halfDay ? (r.fromTime && r.toTime ? ` (half day, ${fmtHm(r.fromTime)} – ${fmtHm(r.toTime)})` : ' (half day)') : '';
  return r.fromDate === r.toDate ? `${fmtDate(r.fromDate)}${half}` : `${fmtDate(r.fromDate)} → ${fmtDate(r.toDate)}`;
};

// The two usual halves of a working day
const HALVES = [
  { label: 'Morning', from: '09:30', to: '14:00' },
  { label: 'Afternoon', from: '14:00', to: '19:00' },
];

function localToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Preview only — the server also leaves out institute holidays.
function daysExcludingSundays(from: string, to: string) {
  let n = 0;
  const end = new Date(`${to}T00:00:00Z`);
  for (const d = new Date(`${from}T00:00:00Z`); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
    if (d.getUTCDay() !== 0) n++;
  }
  return n;
}

const INPUT_CLS = 'w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-purple-400 bg-white';

const LeavePanel: React.FC<{ employeeType: PayslipEmployeeType; employeeId: string }> = ({ employeeType, employeeId }) => {
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState(localToday);
  const [toDate, setToDate] = useState(localToday);
  const [halfDay, setHalfDay] = useState(false);
  const [fromTime, setFromTime] = useState('');
  const [toTime, setToTime] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<string | null>(null);   // 'submit' or the id being withdrawn

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMyLeaves(employeeType, employeeId);
      setRequests(res.requests ?? []);
    } catch (err: any) { toast.error('Failed to load your leave: ' + err.message); }
    finally { setLoading(false); }
  }, [employeeType, employeeId]);

  useEffect(() => { load(); }, [load]);

  const singleDay = fromDate === toDate;
  const previewDays = fromDate && toDate && toDate >= fromDate
    ? (singleDay && halfDay ? 0.5 : daysExcludingSundays(fromDate, toDate))
    : 0;

  const changeFrom = (v: string) => {
    setFromDate(v);
    if (!toDate || toDate < v) setToDate(v);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fromDate || !toDate) { toast.error('Pick the leave dates'); return; }
    if (toDate < fromDate) { toast.error('The "to" date cannot be before the "from" date'); return; }
    if (!reason.trim()) { toast.error('Please give a reason for the leave'); return; }
    if (singleDay && halfDay) {
      if (!fromTime || !toTime) { toast.error('Pick the time of the half day'); return; }
      if (toTime <= fromTime) { toast.error('The half day must end after it starts'); return; }
    }
    setBusy('submit');
    try {
      const res = await requestLeave({
        employeeType, employeeId, fromDate, toDate, halfDay: singleDay && halfDay, reason: reason.trim(),
        ...(singleDay && halfDay ? { fromTime, toTime } : {}),
      });
      toast.success(`Leave request sent (${fmtLeaveDays(res.request.days)}) — waiting for admin approval`);
      setReason('');
      setHalfDay(false);
      setFromTime(''); setToTime('');
      load();
    } catch (err: any) { toast.error(err.message); }
    finally { setBusy(null); }
  };

  const withdraw = async (r: LeaveRequest) => {
    if (!window.confirm(`Withdraw your leave request for ${fmtLeaveDates(r)}?`)) return;
    setBusy(r._id);
    try {
      await cancelLeaveRequest(employeeType, employeeId, r._id);
      toast.success('Leave request withdrawn');
      setRequests(prev => prev.filter(x => x._id !== r._id));
    } catch (err: any) { toast.error(err.message); }
    finally { setBusy(null); }
  };

  const year = String(new Date().getFullYear());
  const approvedThisYear = requests
    .filter(r => r.status === 'Approved' && r.fromDate.startsWith(year))
    .reduce((sum, r) => sum + r.days, 0);
  const pendingCount = requests.filter(r => r.status === 'Pending').length;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 items-start text-left">
      {/* ── Apply ── */}
      <form onSubmit={submit} className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-5 space-y-4">
        <p className="text-xs font-bold text-purple-800 uppercase tracking-wider">Apply for Leave</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">From *</label>
            <input type="date" value={fromDate} onChange={e => changeFrom(e.target.value)} className={INPUT_CLS} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">To *</label>
            <input type="date" value={toDate} min={fromDate} onChange={e => setToDate(e.target.value)} className={INPUT_CLS} />
          </div>
        </div>
        {singleDay && (
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
            <input type="checkbox" checked={halfDay} onChange={e => setHalfDay(e.target.checked)} className="w-4 h-4 accent-purple-700" />
            Half day only
          </label>
        )}
        {singleDay && halfDay && (
          <div className="rounded-xl border border-purple-100 bg-purple-50/50 p-3 space-y-2">
            <p className="text-xs font-semibold text-gray-600">Which hours will you be away? *</p>
            <div className="flex gap-2">
              {HALVES.map(h => {
                const on = fromTime === h.from && toTime === h.to;
                return (
                  <button key={h.label} type="button" onClick={() => { setFromTime(h.from); setToTime(h.to); }}
                    className="flex-1 px-2 py-2 rounded-xl border-2 text-left text-xs font-bold"
                    style={on ? { background: '#2b2240', color: '#fff', borderColor: '#2b2240' } : { background: '#fff', color: '#374151', borderColor: '#e5e7eb' }}>
                    {h.label}<span className="block text-[10px] font-medium opacity-70">{fmtHm(h.from)} – {fmtHm(h.to)}</span>
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="text-[11px] font-semibold text-gray-500">From<input type="time" value={fromTime} onChange={e => setFromTime(e.target.value)} className={INPUT_CLS + ' mt-1'} aria-label="Half day from" /></label>
              <label className="text-[11px] font-semibold text-gray-500">To<input type="time" value={toTime} onChange={e => setToTime(e.target.value)} className={INPUT_CLS + ' mt-1'} aria-label="Half day to" /></label>
            </div>
            {employeeType === 'sales' && <p className="text-[11px] text-gray-500">Once approved, no new leads come to you in these hours.</p>}
          </div>
        )}
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Reason *</label>
          <textarea rows={3} value={reason} onChange={e => setReason(e.target.value)}
            placeholder="e.g. Not feeling well, family function…" className={INPUT_CLS + ' resize-none'} />
        </div>
        <div className="rounded-xl px-3 py-2.5 text-xs bg-purple-50 text-purple-900">
          {previewDays
            ? <>This is <b>{fmtLeaveDays(previewDays)}</b> of leave. Sundays and institute holidays are not counted.</>
            : 'Sundays and institute holidays are not counted as leave.'}
        </div>
        <button type="submit" disabled={busy !== null}
          className="w-full py-3 rounded-xl text-sm font-bold text-white disabled:opacity-60"
          style={{ background: 'linear-gradient(135deg,#F0B429,#FF6535)' }}>
          {busy === 'submit' ? 'Sending…' : 'Send Leave Request'}
        </button>
        <p className="text-[11px] text-gray-400 text-center">Admin approves it — approved leave is shown on your payslip.</p>
      </form>

      {/* ── History ── */}
      <div className="lg:col-span-3 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 flex flex-wrap items-center justify-between gap-2 border-b border-gray-100">
          <p className="font-bold text-gray-800">My Leave Requests</p>
          <p className="text-xs text-gray-500">
            Approved in {year}: <b className="text-gray-800">{fmtLeaveDays(approvedThisYear)}</b>
            {pendingCount > 0 && <> · Pending: <b className="text-amber-700">{pendingCount}</b></>}
          </p>
        </div>
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="w-7 h-7 rounded-full border-purple-200 border-t-purple-600 animate-spin" style={{ borderWidth: 3 }} />
          </div>
        ) : requests.length === 0 ? (
          <div className="p-12 text-center">
            <div className="text-4xl mb-2">🌴</div>
            <p className="text-sm text-gray-400">No leave requests yet.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {requests.map(r => {
              const st = LEAVE_STATUS_STYLE[r.status];
              return (
                <div key={r._id} className="px-5 py-4 space-y-1.5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-800 text-sm">{fmtLeaveDates(r)}</p>
                      <p className="text-xs text-gray-500">{fmtLeaveDays(r.days)} · {r.reason}</p>
                    </div>
                    <span className="text-xs font-bold px-2.5 py-1 rounded-lg flex-shrink-0" style={{ background: st.bg, color: st.color }}>{r.status}</span>
                  </div>
                  {r.status === 'Pending' ? (
                    <button onClick={() => withdraw(r)} disabled={busy !== null}
                      className="text-xs font-semibold text-red-600 hover:underline disabled:opacity-50">
                      {busy === r._id ? 'Withdrawing…' : 'Withdraw request'}
                    </button>
                  ) : r.reviewNote ? (
                    <p className="text-xs text-gray-500">Admin: <span className="text-gray-700">“{r.reviewNote}”</span></p>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeavePanel;
